// TTDSG §25 / GDPR two-tier cookie-consent banner (portfolio analytics standard).
//
// Basic reach measurement (page views, visitor counts) runs cookieless &
// anonymous WITHOUT consent — see src/lib/analytics/posthog.ts. This banner is
// NOT a gate on that basic tier. It only lets a visitor UPGRADE to the
// persistent tier (cross-session cookies + session replay) or turn analytics
// fully off:
//   - Accept  → cookie mystudyfund_consent=granted (1 year)
//               + persistence → localStorage+cookie, opt_in, session recording on
//   - Decline → cookie mystudyfund_consent=denied  (1 year)
//               + posthog.opt_out_capturing() + reset(true)
// Both choices are equally weighted (no dark-pattern "Accept"-only).
//
// The banner only renders when NO choice has been stored yet, so a returning
// visitor is never nagged. PostHog is loaded lazily (dynamic import in
// posthog.ts) and exposed on window.posthog, so we drive it through that
// handle to avoid pulling the library statically into the main bundle.

import { useEffect, useRef, useState } from 'react';
import { CONSENT_COOKIE } from '@/lib/analytics/posthog';
import { isPrerender } from '@/lib/utils/prerender';

const ONE_YEAR = 60 * 60 * 24 * 365;

// Exposed so any other fixed-bottom UI (e.g. DemoMissionPage's mobile sticky
// CTA) can offset itself above this banner instead of being covered by it —
// this banner is z-[100] and full-width, so anything else pinned to the
// viewport bottom is otherwise hidden underneath it for exactly the
// first-time visitors who see it.
const HEIGHT_VAR = '--consent-banner-height';

type PH = {
  set_config?: (opts: Record<string, unknown>) => void;
  opt_in_capturing?: () => void;
  opt_out_capturing?: () => void;
  startSessionRecording?: () => void;
  reset?: (resetDeviceId?: boolean) => void;
  featureFlags?: { setReloadingPaused?: (paused: boolean) => void };
};

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]+)`));
  return m ? decodeURIComponent(m[1]) : null;
}

function writeCookie(name: string, value: string): void {
  document.cookie = `${name}=${value}; Max-Age=${ONE_YEAR}; Path=/; SameSite=Lax`;
}

function getPosthog(): PH | undefined {
  return (window as unknown as { posthog?: PH }).posthog;
}

export default function ConsentBanner() {
  // Start hidden; decide on the client after mount so we never render for a
  // visitor who has already chosen.
  const [visible, setVisible] = useState(false);
  const bannerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isPrerender()) return; // don't bake the banner into the static HTML
    if (readCookie(CONSENT_COOKIE)) return; // already decided
    setVisible(true);
  }, []);

  useEffect(() => {
    if (!visible) {
      document.documentElement.style.setProperty(HEIGHT_VAR, '0px');
      return;
    }
    const el = bannerRef.current;
    if (!el) return;
    const publish = () => document.documentElement.style.setProperty(HEIGHT_VAR, `${el.offsetHeight}px`);
    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(el);
    return () => {
      observer.disconnect();
      document.documentElement.style.setProperty(HEIGHT_VAR, '0px');
    };
  }, [visible]);

  if (!visible) return null;

  function accept() {
    writeCookie(CONSENT_COOKIE, 'granted');
    try {
      // Upgrade from the default cookieless/anonymous layer to PERSISTENT
      // analytics now that we have consent: switch to on-device storage
      // (cross-session identity + funnel tracing) and turn session recording
      // on. Before this, posthog kept everything in memory — nothing was
      // written to the device.
      const ph = getPosthog();
      ph?.set_config?.({
        persistence: 'localStorage+cookie',
        disable_persistence: false, // now allowed to write to the device
      });
      ph?.opt_in_capturing?.(); // no-op unless a prior "denied" opted out
      ph?.startSessionRecording?.();
      ph?.featureFlags?.setReloadingPaused?.(false);
    } catch {
      /* posthog not initialised (no token) — nothing to opt into */
    }
    setVisible(false);
  }

  function decline() {
    writeCookie(CONSENT_COOKIE, 'denied');
    try {
      // Stay opted out + in-memory, and clear any stale posthog storage that a
      // previous visit may have left on the device.
      const ph = getPosthog();
      ph?.opt_out_capturing?.();
      ph?.reset?.(true);
      ph?.featureFlags?.setReloadingPaused?.(true);
    } catch {
      /* no-op */
    }
    setVisible(false);
  }

  return (
    <div
      ref={bannerRef}
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      className="fixed inset-x-0 bottom-0 z-[100] border-t border-gray-200 bg-white/95 backdrop-blur px-4 py-3 shadow-[0_-2px_12px_rgba(0,0,0,0.06)]"
    >
      <div className="mx-auto flex max-w-5xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs leading-relaxed text-gray-600">
          We measure basic, anonymous, cookie-free reach stats (nothing is stored on your device).{' '}
          &ldquo;Accept&rdquo; additionally allows cookies for improved analytics (e.g. cross-session
          insights &amp; session replay, PostHog &middot; EU-hosted). &ldquo;Decline&rdquo; turns all
          analytics off.{' '}
          <a href="/privacy" className="underline hover:text-gray-900">
            Privacy policy
          </a>
        </p>
        <div className="flex flex-none gap-2">
          <button
            type="button"
            onClick={decline}
            className="rounded-md border border-gray-300 bg-white px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Decline
          </button>
          <button
            type="button"
            onClick={accept}
            className="rounded-md bg-custom-blue px-4 py-1.5 text-sm font-semibold text-white hover:bg-custom-blue-hover"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}
